// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/visibleComments.test.ts
/** Minimum fields for reject/visibility filtering (ticket + AskPunttReview comments). */
export type ReviewCommentVisibilityFields = {
  isDeleted?: boolean;
  liked?: boolean | null;
  isAI?: boolean;
  /** Set when a reviewer already rejected this finding, here or elsewhere. */
  previouslyRejected?: { judgmentId: string } | null;
  /** Set when a reviewer already confirmed this finding, here or elsewhere. */
  previouslyConfirmed?: { judgmentId: string } | null;
  /** Set when an earlier review of this artwork raised the same finding. */
  earlierComment?: { reviewId: string } | null;
  /** warhol's intent id; `<raiser>-carried-<commentId>` on a carried finding. */
  intentId?: string;
};

/** Soft-deleted or reviewer-rejected comments stay out of review UI lists. */
export function isVisibleReviewComment(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return !comment.isDeleted && comment.liked !== false;
}

export function isRejectedReviewComment(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return !comment.isDeleted && comment.liked === false;
}

export function visibleReviewComments<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
): T[] {
  return (comments ?? []).filter(isVisibleReviewComment);
}

export function rejectedReviewComments<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
): T[] {
  return (comments ?? []).filter(isRejectedReviewComment);
}

export function rejectedReviewCommentCount(
  comments: ReviewCommentVisibilityFields[] | undefined,
): number {
  return rejectedReviewComments(comments).length;
}

/**
 * Findings she has already ruled on, sorted below the ones she has not.
 *
 * Demoted, never dropped: a finding she rejected before still appears, so a
 * wrongly learned rejection is visible rather than silently hiding a real
 * defect. Stable within each group, so the order she is used to survives.
 *
 * why here and not only in warhol: warhol sorts the `ReviewOutcome`, and the
 * panel renders christo's comments, which are a different list. Sorting only
 * there put demoted findings back at the top of the one she reads.
 */
export function demotedLast<T extends ReviewCommentVisibilityFields>(
  comments: T[],
): T[] {
  return [...comments].sort((a, b) => {
    const left = a.previouslyRejected ? 1 : 0;
    const right = b.previouslyRejected ? 1 : 0;
    return left - right;
  });
}

/** Comments shown in the review panel carousel (optionally includes rejected). */
export function reviewCommentsForPanel<T extends ReviewCommentVisibilityFields>(
  comments: T[] | undefined,
  includeRejected: boolean,
): T[] {
  const list = comments ?? [];
  if (includeRejected) {
    return demotedLast(list.filter((c) => !c.isDeleted));
  }
  return demotedLast(visibleReviewComments(list));
}

/**
 * Where a finding sits in the Ask v2 findings list, in display order.
 *
 * A past judgment outranks "raised before": a finding she rejected (or
 * confirmed) keeps its own section even when an earlier review raised it too,
 * so the Rejected section is unchanged by the Earlier one. `earlierComment` is
 * what warhol sets on a finding matching a comment of an earlier review of the
 * same artwork -- a comment it carried forward and one it raised afresh alike.
 * A carried finding whose match did not fire has no `earlierComment`, but its
 * intent id still names the earlier comment it carries (spec 0063), so it
 * counts as raised before too.
 */
export const REVIEW_COMMENT_GROUPS = [
  "new",
  "earlier",
  "confirmed",
  "rejected",
] as const;
export type ReviewCommentGroup = (typeof REVIEW_COMMENT_GROUPS)[number];

export function reviewCommentGroup(
  comment: ReviewCommentVisibilityFields,
): ReviewCommentGroup {
  if (comment.previouslyRejected) return "rejected";
  if (comment.previouslyConfirmed) return "confirmed";
  if (comment.earlierComment || isCarriedIntentId(comment.intentId)) {
    // why: the Earlier section starts closed and its pins are hidden with it, so
    // a finding she judged in THIS review would vanish once the selection moves
    // on -- her reject must stay on screen, grayed, with Restore one click away.
    if (comment.liked === false) return "rejected";
    if (comment.liked === true) return "confirmed";
    return "earlier";
  }
  return "new";
}

// why: warhol's `carried_intent_id` writes `<raiser>-carried-<commentId>` with
// a 24-hex Mongo id; matching the whole tail keeps a raiser or rule slug that
// merely contains "carried" out of the Earlier section.
const CARRIED_INTENT_ID = /-carried-[0-9a-f]{24}$/;

function isCarriedIntentId(intentId: string | undefined): boolean {
  return intentId != null && CARRIED_INTENT_ID.test(intentId);
}

/** A finding an earlier review of this artwork already raised, and nobody has judged. */
export function isEarlierReviewComment(
  comment: ReviewCommentVisibilityFields,
): boolean {
  return reviewCommentGroup(comment) === "earlier";
}

/**
 * Comments the Ask v2 review panel draws: every live finding, the ones
 * rejected in this review included, in the order the list shows its sections
 * (new, earlier, approved, rejected).
 *
 * why a separate path and not `reviewCommentsForPanel(list, true)` at the
 * call site: v1 still hides a rejection by default behind its toggle, while
 * v2 never hides one -- it draws it grayed in its place, so a reject stays
 * visible and can be undone without first finding a control. Two names keep
 * a later v1 change from quietly hiding v2 findings again.
 *
 * why sorted by section, not left in stored order: the list numbers findings
 * in this order and the artwork pins carry the same numbers, so a section that
 * is not contiguous would number its cards 6, 9, 14 instead of 6, 7, 8.
 * Stable within a section.
 */
export function askV2ReviewPanelComments<
  T extends ReviewCommentVisibilityFields,
>(comments: T[] | undefined): T[] {
  const rank = (comment: T) =>
    REVIEW_COMMENT_GROUPS.indexOf(reviewCommentGroup(comment));
  return (comments ?? [])
    .filter((c) => !c.isDeleted)
    .sort((a, b) => rank(a) - rank(b));
}

export function visibleAiCommentCount(
  comments: (ReviewCommentVisibilityFields & { isAI?: boolean })[] | undefined,
): number {
  return visibleReviewComments(comments).filter((c) => c.isAI).length;
}

export function visibleHumanCommentCount(
  comments: (ReviewCommentVisibilityFields & { isAI?: boolean })[] | undefined,
): number {
  return visibleReviewComments(comments).filter((c) => !c.isAI).length;
}
